import { useState, useCallback, useEffect } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { httpClient } from "../../HttpClient";

// Distinct du login Google (users/google, ID token seul) : ici on demande en
// plus l'accès Gmail (lecture + envoi), via le flow "auth-code" pour obtenir
// un refresh token stocké côté backend (voir plan Gmail / GoogleMailAccount).
function useGmailAccount() {
  const [connected, setConnected] = useState(false);
  const [email, setEmail] = useState(null);
  const [hasModifyScope, setHasModifyScope] = useState(false);
  const [messages, setMessages] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [messageDetails, setMessageDetails] = useState({}); // { [id]: { loading, error, data } }

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await httpClient.get("/api/users/me/google-mail/status");
      setConnected(data.connected);
      setEmail(data.email);
      setHasModifyScope(!!data.hasModifyScope);
      return data.connected;
    } catch (err) {
      console.error("Erreur statut Gmail :", err);
      return false;
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const fetchMessages = useCallback(async () => {
    setLoadingMessages(true);
    setError(null);
    try {
      const { data } = await httpClient.get("/api/users/me/google-mail/messages");
      setMessages(data.messages || []);
      setNextPageToken(data.nextPageToken || null);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors du chargement des messages.");
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Charge la page suivante (Gmail ne renvoie qu'un lot par appel — sans
  // suivre le curseur nextPageToken, seuls les tout derniers mails
  // remontent jamais).
  const loadMore = useCallback(async () => {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const { data } = await httpClient.get("/api/users/me/google-mail/messages", {
        params: { pageToken: nextPageToken },
      });
      setMessages((prev) => [...prev, ...(data.messages || [])]);
      setNextPageToken(data.nextPageToken || null);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors du chargement des messages.");
    } finally {
      setLoadingMore(false);
    }
  }, [nextPageToken, loadingMore]);

  // Corps complet + pièces jointes d'un message, chargés à la demande
  // (au clic) et mis en cache pour ne pas re-fetcher en rouvrant le même mail.
  const fetchMessageDetail = useCallback(async (id) => {
    setMessageDetails((prev) => ({ ...prev, [id]: { loading: true, error: null, data: prev[id]?.data || null } }));
    try {
      const { data } = await httpClient.get(`/api/users/me/google-mail/messages/${id}`);
      setMessageDetails((prev) => ({ ...prev, [id]: { loading: false, error: null, data } }));
    } catch (err) {
      setMessageDetails((prev) => ({
        ...prev,
        [id]: { loading: false, error: err.response?.data?.message || "Erreur lors du chargement du message.", data: null },
      }));
    }
  }, []);

  const connect = useGoogleLogin({
    flow: "auth-code",
    // gmail.modify (en plus de readonly/send) : nécessaire pour classer et
    // archiver réellement le mail côté Gmail (retrait de l'étiquette INBOX),
    // pas seulement le masquer dans MyALFA.
    scope: "https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify",
    // access_type/prompt forcent Google à renvoyer un refresh_token à chaque
    // fois (sans ça, un consentement déjà donné ne renvoie qu'un access
    // token à courte durée, inutilisable pour un accès durable).
    access_type: "offline",
    prompt: "consent",
    onSuccess: async (resp) => {
      setConnecting(true);
      setError(null);
      try {
        await httpClient.post("/api/users/me/google-mail/connect", { code: resp.code });
        await refreshStatus();
        await fetchMessages();
      } catch (err) {
        setError(err.response?.data?.message || "Erreur lors de la connexion à Gmail.");
      } finally {
        setConnecting(false);
      }
    },
    onError: () => {
      setError("Échec de la connexion Google.");
      setConnecting(false);
    },
  });

  const sendMail = useCallback(async ({ to, subject, body }) => {
    await httpClient.post("/api/users/me/google-mail/send", { to, subject, body });
  }, []);

  // Retire l'étiquette INBOX côté Gmail (archivage réel, pas seulement le
  // masquage local dans MyALFA) — nécessite le scope gmail.modify, absent
  // des connexions faites avant son ajout ; le backend renvoie alors un 403
  // explicite demandant de se reconnecter, laissé tel quel pour l'appelant.
  const archiveMessage = useCallback(async (id) => {
    await httpClient.post(`/api/users/me/google-mail/messages/${id}/archive`);
  }, []);

  const disconnect = useCallback(async () => {
    await httpClient.delete("/api/users/me/google-mail");
    setConnected(false);
    setEmail(null);
    setHasModifyScope(false);
    setMessages([]);
    setNextPageToken(null);
    setMessageDetails({});
  }, []);

  return {
    connected,
    email,
    hasModifyScope,
    messages,
    nextPageToken,
    loadingMessages,
    loadingMore,
    connecting,
    error,
    connect,
    fetchMessages,
    loadMore,
    messageDetails,
    fetchMessageDetail,
    sendMail,
    archiveMessage,
    disconnect,
  };
}

export default useGmailAccount;
